import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { client, queries } from '../lib/sanity';
import { parseExcelCostos, cruzarProductos, inferirMarca, inferirCategoria, generarSlug } from '../lib/excelParser';
import { calcularPrecios } from '../lib/precios';
import { useTasaStore } from '../lib/store';
import './AdminTasa.css';

const SHEETJS_CDN = 'https://cdn.jsdelivr.net/npm/xlsx@0.18.5/dist/xlsx.full.min.js';

const SUCURSALES = ['NUOVOCELL', 'MORON', 'STAND', 'FREE MARKET', 'LARA', 'YARACAL', 'GOAJIROS', 'GRANJA', 'MIRIMIRE'];

export default function AdminCostos() {
  const [stage, setStage] = useState('upload');
  // upload → parsing → preview → applying → done

  const [sheetReady, setSheetReady] = useState(!!window.XLSX);
  const [sucursalPref, setSucursalPref] = useState('NUOVOCELL');
  const [error, setError] = useState(null);
  const [parsedData, setParsedData] = useState(null);  // {productos, sucursalesEncontradas}
  const [cruce, setCruce] = useState(null);            // {matches, dudosos, soloExcel, soloSanity}
  const [productosSanity, setProductosSanity] = useState([]);

  // Selección de cada dudoso: true = aceptar, false = rechazar
  const [decisionDudosos, setDecisionDudosos] = useState({});
  // Excluir matches automáticos individuales
  const [excluirMatches, setExcluirMatches] = useState({});
  // Crear productos nuevos: descripcion → bool
  const [decisionCreate, setDecisionCreate] = useState({});
  // Datos editables por producto a crear: descripcion → {marca, categoria}
  const [creacionDatos, setCreacionDatos] = useState({});

  const [password, setPassword] = useState('');
  const [applyMsg, setApplyMsg] = useState(null);

  // Config para calcular preview de precios
  const tasaCambio  = useTasaStore(s => s.tasa);
  const tasaInterna = useTasaStore(s => s.tasaInterna);
  const margenBajo  = useTasaStore(s => s.margenCasheaBajo);
  const margenAlto  = useTasaStore(s => s.margenCasheaAlto);
  const umbral      = useTasaStore(s => s.umbralCashea);
  const margenKrece = useTasaStore(s => s.margenKrece);
  const configPrecios = { tasaCambio, tasaInterna, margenCasheaBajo: margenBajo, margenCasheaAlto: margenAlto, umbralCashea: umbral, margenKrece };

  // Cargar SheetJS dinámicamente
  useEffect(() => {
    if (window.XLSX) { setSheetReady(true); return; }
    const script = document.createElement('script');
    script.src = SHEETJS_CDN;
    script.onload = () => setSheetReady(true);
    script.onerror = () => setError('No se pudo cargar la librería de Excel (revisa tu conexión)');
    document.body.appendChild(script);
    return () => { document.body.removeChild(script); };
  }, []);

  // Cargar catálogo de Sanity al inicio
  useEffect(() => {
    client.fetch(queries.allProducts).then(setProductosSanity).catch(err => {
      setError('Error cargando catálogo de Sanity: ' + err.message);
    });
  }, []);

  const handleFile = useCallback(async (file) => {
    if (!file) return;
    if (!window.XLSX) { setError('La librería de Excel aún no está lista'); return; }
    setError(null);
    setStage('parsing');

    try {
      const buf = await file.arrayBuffer();
      const wb = window.XLSX.read(buf, { type: 'array' });
      const parsed = parseExcelCostos(wb, sucursalPref);
      setParsedData(parsed);

      const c = cruzarProductos(parsed.productos, productosSanity);
      setCruce(c);

      // Inicializar decisiones: dudosos en "rechazar" por defecto (más seguro)
      const initDec = {};
      c.dudosos.forEach(d => { initDec[d.sanity._id] = false; });
      setDecisionDudosos(initDec);
      setExcluirMatches({});
      // Creación: por defecto NO crear ninguno. El usuario marca los que quiera.
      setDecisionCreate({});
      setCreacionDatos({});

      setStage('preview');
    } catch (e) {
      setError('Error parseando el Excel: ' + e.message);
      setStage('upload');
    }
  }, [sucursalPref, productosSanity]);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    const file = e.dataTransfer?.files?.[0];
    if (file) handleFile(file);
  }, [handleFile]);

  const handleApply = async () => {
    if (!password) { setApplyMsg({ type: 'error', text: 'Ingresa la contraseña' }); return; }
    setStage('applying');
    setApplyMsg(null);

    // Construir updates: matches no-excluidos + dudosos aceptados
    const updates = [];
    cruce.matches.forEach(m => {
      if (!excluirMatches[m.sanity._id]) {
        updates.push({ _id: m.sanity._id, costoUSD: m.excel.costoUSD, unsetPrecio: true });
      }
    });
    cruce.dudosos.forEach(d => {
      if (decisionDudosos[d.sanity._id]) {
        updates.push({ _id: d.sanity._id, costoUSD: d.excel.costoUSD, unsetPrecio: true });
      }
    });

    // Construir creates: productos del Excel marcados para crear
    const creates = [];
    cruce.soloExcel.forEach(p => {
      if (decisionCreate[p.descripcion]) {
        const datos = creacionDatos[p.descripcion] || {
          marca: inferirMarca(p.descripcion) || 'Otro',
          categoria: inferirCategoria(p.descripcion),
        };
        creates.push({
          nombre: p.descripcion,
          costoUSD: p.costoUSD,
          marca: datos.marca,
          categoria: datos.categoria,
          slug: generarSlug(p.descripcion),
        });
      }
    });

    if (updates.length === 0 && creates.length === 0) {
      setApplyMsg({ type: 'error', text: 'No hay operaciones seleccionadas' });
      setStage('preview');
      return;
    }

    try {
      const res = await fetch('/api/admin/apply-costos', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ password, updates, creates }),
      });
      const data = await res.json();
      if (data.success) {
        const parts = [];
        if (data.updated) parts.push(`${data.updated} actualizados`);
        if (data.created) parts.push(`${data.created} creados (ocultos hasta completarlos en Sanity)`);
        setApplyMsg({ type: 'success', text: parts.join(' + ') });
        setPassword('');
        setStage('done');
      } else {
        setApplyMsg({ type: 'error', text: data.error || 'Error al aplicar' });
        setStage('preview');
      }
    } catch (e) {
      setApplyMsg({ type: 'error', text: 'Error de red: ' + e.message });
      setStage('preview');
    }
  };

  // ============ RENDER ============

  return (
    <div className="admin-tasa">
      <div className="admin-tasa__card" style={{ maxWidth: 920 }}>
        <div className="admin-tasa__logo">
          <img src="/logos/nuovocell-logo.png" alt="Nuovocell" />
        </div>
        <h1 className="admin-tasa__title">Actualizar costos desde Excel</h1>
        <p style={{ textAlign: 'center', fontSize: 13, color: '#666', marginBottom: 20 }}>
          <Link to="/admin/tasa">Tasa BCV</Link> · <Link to="/admin/precios">Márgenes</Link> · Costos
        </p>

        {error && <div className="admin-tasa__msg admin-tasa__msg--error">{error}</div>}

        {/* ===== ETAPA: UPLOAD ===== */}
        {stage === 'upload' && (
          <>
            <div style={{ marginBottom: 16 }}>
              <label style={{ display: 'block', fontSize: 13, color: '#555', marginBottom: 6 }}>
                Sucursal preferida para tomar el costo (si el producto está en varias):
              </label>
              <select
                value={sucursalPref}
                onChange={e => setSucursalPref(e.target.value)}
                style={{ width: '100%', padding: '8px 12px', border: '1px solid #ddd', borderRadius: 6 }}
              >
                {SUCURSALES.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>

            <div
              onDragOver={e => e.preventDefault()}
              onDrop={handleDrop}
              style={{
                border: '2px dashed #ccd', borderRadius: 8, padding: '40px 20px',
                textAlign: 'center', cursor: 'pointer', background: '#fafbfc'
              }}
              onClick={() => document.getElementById('xlsx-input').click()}
            >
              <p style={{ margin: '0 0 12px', fontSize: 15, color: '#555' }}>
                📊 Arrastra el Excel aquí o haz clic para seleccionar
              </p>
              <p style={{ margin: 0, fontSize: 12, color: '#999' }}>
                Formato: hoja FIFO + hojas de sucursal (MORON, STAND, etc.)
              </p>
              <input
                id="xlsx-input"
                type="file"
                accept=".xlsx,.xls"
                onChange={e => handleFile(e.target.files[0])}
                style={{ display: 'none' }}
              />
            </div>

            {!sheetReady && <p style={{ fontSize: 12, color: '#999', textAlign: 'center', marginTop: 12 }}>Cargando librería de Excel...</p>}
            {productosSanity.length > 0 && (
              <p style={{ fontSize: 12, color: '#999', textAlign: 'center', marginTop: 12 }}>
                Catálogo cargado: {productosSanity.length} productos en Sanity
              </p>
            )}
          </>
        )}

        {/* ===== ETAPA: PARSING ===== */}
        {stage === 'parsing' && (
          <p style={{ textAlign: 'center', padding: '40px 0' }}>Procesando Excel...</p>
        )}

        {/* ===== ETAPA: PREVIEW ===== */}
        {stage === 'preview' && cruce && (
          <>
            {/* Resumen */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5,1fr)', gap: 10, marginBottom: 20 }}>
              <StatCard label="Excel → catálogo" valor={parsedData.totalCatalogoFifo} />
              <StatCard label="Con costo" valor={parsedData.productos.length} />
              <StatCard label="Match con Sanity" valor={cruce.matches.length} highlight="green" />
              <StatCard label="Dudosos" valor={cruce.dudosos.length} highlight="amber" />
              <StatCard label="Nuevos a crear" valor={cruce.soloExcel.length} highlight="blue" />
            </div>

            {/* MATCHES */}
            {cruce.matches.length > 0 && (
              <Section titulo={`Matches automáticos (${cruce.matches.length})`}>
                <table style={tableStyle}>
                  <thead><tr>
                    <th style={{ width: 30 }}></th>
                    <th>Producto en Sanity</th>
                    <th style={tdRight}>Precio actual</th>
                    <th style={tdRight}>Costo Excel</th>
                    <th style={tdRight}>Precio nuevo</th>
                    <th style={tdRight}>Δ</th>
                  </tr></thead>
                  <tbody>
                    {cruce.matches.map(m => {
                      const incluido = !excluirMatches[m.sanity._id];
                      const nuevo = calcularPrecios({ costoUSD: m.excel.costoUSD }, configPrecios);
                      const precioActual = m.sanity.precio;
                      const delta = (precioActual && nuevo) ? ((nuevo.casheaUSD - precioActual) / precioActual * 100) : null;
                      return (
                        <tr key={m.sanity._id} style={{ opacity: incluido ? 1 : 0.35 }}>
                          <td>
                            <input type="checkbox" checked={incluido}
                              onChange={e => setExcluirMatches(s => ({ ...s, [m.sanity._id]: !e.target.checked }))} />
                          </td>
                          <td>
                            <div>{m.sanity.nombre}</div>
                            {!m.exacto && (
                              <div style={{ fontSize: 11, color: '#999' }}>
                                ↔ {m.excel.descripcion} <small>({Math.round(m.score * 100)}%)</small>
                              </div>
                            )}
                          </td>
                          <td style={tdRight}>{precioActual ? `$${precioActual.toFixed(2)}` : '—'}</td>
                          <td style={tdRight}>${m.excel.costoUSD.toFixed(2)}</td>
                          <td style={{ ...tdRight, fontWeight: 500 }}>{nuevo ? `$${nuevo.casheaUSD.toFixed(2)}` : '—'}</td>
                          <td style={{ ...tdRight, color: delta > 0 ? '#c0392b' : delta < 0 ? '#27ae60' : '#999' }}>
                            {delta !== null ? `${delta > 0 ? '+' : ''}${delta.toFixed(1)}%` : '—'}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </Section>
            )}

            {/* DUDOSOS */}
            {cruce.dudosos.length > 0 && (
              <Section titulo={`Matches dudosos — requieren tu confirmación (${cruce.dudosos.length})`}>
                <p style={{ fontSize: 12, color: '#888', margin: '0 0 8px' }}>
                  Por defecto NO se aplican. Marca los que sean correctos.
                </p>
                <table style={tableStyle}>
                  <thead><tr>
                    <th style={{ width: 30 }}></th>
                    <th>Sanity</th>
                    <th>Excel</th>
                    <th style={tdRight}>Score</th>
                    <th style={tdRight}>Costo</th>
                  </tr></thead>
                  <tbody>
                    {cruce.dudosos.map(d => (
                      <tr key={d.sanity._id} style={{ opacity: decisionDudosos[d.sanity._id] ? 1 : 0.6 }}>
                        <td>
                          <input type="checkbox" checked={!!decisionDudosos[d.sanity._id]}
                            onChange={e => setDecisionDudosos(s => ({ ...s, [d.sanity._id]: e.target.checked }))} />
                        </td>
                        <td>{d.sanity.nombre}</td>
                        <td style={{ color: '#666' }}>{d.excel.descripcion}</td>
                        <td style={tdRight}>{Math.round(d.score * 100)}%</td>
                        <td style={tdRight}>${d.excel.costoUSD.toFixed(2)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </Section>
            )}

            {/* SOLO EXCEL — Crear nuevos productos */}
            {cruce.soloExcel.length > 0 && (
              <Section titulo={`En Excel pero NO en Sanity — Crear como nuevos (${cruce.soloExcel.length})`}>
                <p style={{ fontSize: 12, color: '#888', margin: '0 0 8px' }}>
                  Los productos seleccionados se crearán en Sanity <strong>ocultos del catálogo</strong> (disponible=false).
                  Marca y categoría se infieren del nombre — puedes corregirlas antes de aplicar.
                  Después, el equipo entra a Sanity para añadirles imagen, descripción y publicarlos.
                </p>
                <div style={{ marginBottom: 8, display: 'flex', gap: 10, fontSize: 12 }}>
                  <button type="button" onClick={() => {
                    const next = {};
                    cruce.soloExcel.forEach(p => { next[p.descripcion] = true; });
                    setDecisionCreate(next);
                  }} style={smallBtnStyle}>Marcar todos</button>
                  <button type="button" onClick={() => setDecisionCreate({})} style={smallBtnStyle}>Desmarcar todos</button>
                </div>
                <table style={tableStyle}>
                  <thead><tr>
                    <th style={{ width: 30 }}></th>
                    <th>Nombre (Excel)</th>
                    <th style={tdRight}>Costo</th>
                    <th>Marca</th>
                    <th>Categoría</th>
                  </tr></thead>
                  <tbody>
                    {cruce.soloExcel.map(p => {
                      const sel = !!decisionCreate[p.descripcion];
                      const datos = creacionDatos[p.descripcion] || {
                        marca: inferirMarca(p.descripcion) || 'Otro',
                        categoria: inferirCategoria(p.descripcion),
                      };
                      return (
                        <tr key={p.descripcion} style={{ opacity: sel ? 1 : 0.55 }}>
                          <td>
                            <input type="checkbox" checked={sel}
                              onChange={e => setDecisionCreate(s => ({ ...s, [p.descripcion]: e.target.checked }))} />
                          </td>
                          <td>{p.descripcion}</td>
                          <td style={tdRight}>${p.costoUSD.toFixed(2)}</td>
                          <td>
                            <select disabled={!sel} value={datos.marca}
                              onChange={e => setCreacionDatos(s => ({ ...s, [p.descripcion]: { ...datos, marca: e.target.value } }))}
                              style={miniSelectStyle}>
                              {['Apple','Samsung','Xiaomi / Redmi','Honor','Infinix','Tecno','Itel','Huawei','Dell','HP','Lenovo','Otro'].map(m =>
                                <option key={m} value={m}>{m}</option>)}
                            </select>
                          </td>
                          <td>
                            <select disabled={!sel} value={datos.categoria}
                              onChange={e => setCreacionDatos(s => ({ ...s, [p.descripcion]: { ...datos, categoria: e.target.value } }))}
                              style={miniSelectStyle}>
                              {['smartphones','laptops','accesorios','tablets','internet','usados','repuestos','filamentos'].map(c =>
                                <option key={c} value={c}>{c}</option>)}
                            </select>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </Section>
            )}

            {/* APLICAR */}
            <div style={{ marginTop: 20, padding: 16, background: '#f5f7fa', borderRadius: 8 }}>
              <div className="admin-tasa__group" style={{ marginBottom: 12 }}>
                <label>Contraseña para confirmar</label>
                <input type="password" value={password} onChange={e => setPassword(e.target.value)} placeholder="Contraseña de administrador" />
              </div>
              {applyMsg && <div className={`admin-tasa__msg admin-tasa__msg--${applyMsg.type}`}>{applyMsg.text}</div>}
              <div style={{ display: 'flex', gap: 10 }}>
                <button onClick={() => { setStage('upload'); setCruce(null); setParsedData(null); }}
                  style={{ flex: 1, padding: '10px 20px', borderRadius: 6, border: '1px solid #ccd', background: 'white', cursor: 'pointer' }}>
                  Cancelar
                </button>
                <button onClick={handleApply}
                  className="admin-tasa__btn" style={{ flex: 2 }}>
                  Aplicar {countApplicables(cruce, decisionDudosos, excluirMatches, decisionCreate)} cambios
                </button>
              </div>
            </div>
          </>
        )}

        {/* ===== ETAPA: APPLYING ===== */}
        {stage === 'applying' && (
          <p style={{ textAlign: 'center', padding: '40px 0' }}>Aplicando cambios en Sanity...</p>
        )}

        {/* ===== ETAPA: DONE ===== */}
        {stage === 'done' && (
          <div style={{ textAlign: 'center', padding: 20 }}>
            {applyMsg && <div className={`admin-tasa__msg admin-tasa__msg--${applyMsg.type}`}>{applyMsg.text}</div>}
            <button onClick={() => { setStage('upload'); setCruce(null); setParsedData(null); setApplyMsg(null); }}
              className="admin-tasa__btn" style={{ marginTop: 12 }}>
              Subir otro Excel
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

// ============ Subcomponentes y utils ============

function StatCard({ label, valor, highlight }) {
  const palette = {
    green: { bg: '#EAF3DE', fg: '#3B6D11' },
    amber: { bg: '#FAEEDA', fg: '#854F0B' },
    blue:  { bg: '#E6F1FB', fg: '#0C447C' },
  };
  const { bg = '#f5f7fa', fg = '#333' } = palette[highlight] || {};
  return (
    <div style={{ background: bg, color: fg, padding: 12, borderRadius: 6, textAlign: 'center' }}>
      <div style={{ fontSize: 11, opacity: 0.7 }}>{label}</div>
      <div style={{ fontSize: 24, fontWeight: 500 }}>{valor}</div>
    </div>
  );
}

function Section({ titulo, children }) {
  return (
    <div style={{ marginTop: 20, border: '1px solid #e3e8ee', borderRadius: 8, padding: 14 }}>
      <h3 style={{ margin: '0 0 12px', fontSize: 14, fontWeight: 500 }}>{titulo}</h3>
      {children}
    </div>
  );
}

const tableStyle = { width: '100%', fontSize: 13, borderCollapse: 'collapse' };
const tdRight = { textAlign: 'right', padding: '4px 6px' };
const miniSelectStyle = { fontSize: 12, padding: '3px 6px', border: '1px solid #ccd', borderRadius: 4, background: 'white', maxWidth: 130 };
const smallBtnStyle = { fontSize: 11, padding: '4px 10px', border: '1px solid #ccd', borderRadius: 4, background: 'white', cursor: 'pointer' };

function countApplicables(cruce, decisionDudosos, excluirMatches, decisionCreate) {
  let n = 0;
  cruce.matches.forEach(m => { if (!excluirMatches[m.sanity._id]) n++; });
  cruce.dudosos.forEach(d => { if (decisionDudosos[d.sanity._id]) n++; });
  cruce.soloExcel.forEach(p => { if (decisionCreate[p.descripcion]) n++; });
  return n;
}
